/**
 * Runtime asset resolution — read `skaile.yaml`, scan all declared repositories
 * (including the built-in `factory-assets/` directory as an implicit default),
 * and produce a unified view of connector catalog entries plus the implicit refs
 * that the `skaile.yaml` `connectors:` section adds to the dependency graph.
 *
 * This module is the single source of truth for "which connectors does this
 * project see at runtime, and where do their implementations live on disk".
 * Both the runner (serve, REPL, flow) and the platform's mid-session
 * `add_resource` flow consume `RuntimeAssetsResult.catalogEntries` and pass
 * it into `ConnectorManager` for catalog-based resolution.
 *
 * `factory-assets/` is the single first-party content tree (built-in connectors
 * + first-party skills + bundles) shipped inside the `@skaile/workspaces`
 * package — not a standalone package.
 *
 * It resolves to an **ordered list of roots** ({@link factoryAssetRoots}), not a
 * single dir. The bundled tree is always the baseline; an optional
 * `SKAILE_FACTORY_ASSETS_DIR` (legacy: `SKAILE_BASE_ASSETS_DIR`) is layered
 * **in front** as an additive override source — the agent container points it at
 * `/app/factory-assets/` (or a bind-mounted host dir) to ship updated skills /
 * flows / bundles / connector manifests without rebuilding the image. The two
 * roots resolve uniformly across every consumer (connector catalog scan, skill /
 * flow / completion discovery, the asset-manager factory source):
 *   1. `SKAILE_FACTORY_ASSETS_DIR` env dir, when set and existing (override layer).
 *   2. the bundled tree via {@link resolvePackagedAssetsDir} — the in-repo source
 *      `packages/workspaces/factory-assets/` during dev, else the published
 *      `dist/factory-assets/` after install (always-present baseline).
 *
 * Override semantics are **env-first**: on a name conflict the env root wins and
 * the bundled root fills the rest. Connector *code* still loads via the package's
 * static `exports` map, so the env root can override/add skills, flows, bundles,
 * and connector **manifests/metadata**, but cannot ship a new executable
 * connector — its `.js` is not on the package's import graph.
 */

import { existsSync, readFileSync } from "node:fs";
import { lstat, stat } from "node:fs/promises";
import { createRequire } from "node:module";
import { dirname, join, resolve as resolvePath } from "node:path";
import { fileURLToPath } from "node:url";
import { tryWithFsDeadline } from "./fs-deadline.js";
import { scanDirectory } from "./manifest.js";
import type { CatalogEntry } from "./models.js";
import { getGlobalCacheDir, readLinks, sourceCacheKey } from "./repo-manager.js";
import type { SourceDeclaration, SourceEntry } from "./workspace-config.js";
import { resolveSkWorkspaceConfig } from "./workspace-config.js";

/**
 * Map declared `sources[]` to internal `SourceDeclaration`s keyed by the
 * collision-free {@link sourceCacheKey} (`<host>/<owner>/<repo>`), pointing at
 * the unified machine-cache clone path (`getGlobalCacheDir()/<key>/`). Keyed by
 * the same function every other cache writer/reader uses so the path resolves.
 */
function sourcesToDeclarations(
  sources: SourceEntry[] | undefined,
): Record<string, SourceDeclaration> {
  const sourcesDir = getGlobalCacheDir();
  const out: Record<string, SourceDeclaration> = {};
  for (const s of sources ?? []) {
    const key = sourceCacheKey(s.url);
    if (out[key]) continue;
    out[key] = { path: join(sourcesDir, key), url: s.url, branch: s.pin };
  }
  return out;
}

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Catalog entry filtered to mount/connector kinds. Same shape as `CatalogEntry`.
 * @docLink packages/core/concepts#runtime-catalog-entry
 */
export type RuntimeCatalogEntry = CatalogEntry;

/**
 * Implicit catalog reference derived from a `connectors:` declaration in `skaile.yaml`.
 * Each connector driver becomes an implicit ref that must be resolved in the catalog.
 * @docLink packages/core/concepts#runtime-asset-ref
 */
export interface RuntimeAssetRef {
  kind: "connector";
  /** Driver name — matches `RuntimeCatalogEntry.name`. */
  name: string;
  /** Source declaration id from `skaile.yaml`. */
  declarationId: string;
}

/**
 * Aggregated npm packages required by all resolved mount/connector adapters.
 * Populated from `metadata.npm_deps` in each matched catalog entry.
 * @docLink packages/core/concepts#runtime-npm-deps
 */
export interface RuntimeNpmDeps {
  required: string[];
  optional: string[];
}

/**
 * Non-fatal warning emitted during `resolveRuntimeAssets` when a repo is unavailable
 * or a driver is not found in any catalog.
 * @docLink packages/core/concepts#runtime-asset-warning
 */
export interface RuntimeAssetWarning {
  /** Stable code for filtering / structured logging. */
  code: "missing_driver" | "repo_unavailable" | "invalid_repo_decl";
  message: string;
  /** Declaration id when applicable. */
  declarationId?: string;
  /** Driver / repository name when applicable. */
  name?: string;
}

/**
 * Repository name used for the implicit built-in `factory-assets` catalog entry
 * (the in-package connector tree).
 * @docLink packages/core/concepts#factory-assets-repo-name
 */
export const FACTORY_ASSETS_REPO_NAME = "factory-assets";

/**
 * Repository name for the optional `SKAILE_FACTORY_ASSETS_DIR` override layer,
 * scanned ahead of (and overriding) the bundled {@link FACTORY_ASSETS_REPO_NAME}.
 */
const FACTORY_ASSETS_OVERRIDE_REPO_NAME = "factory-assets:override";

/**
 * Result of `resolveRuntimeAssets`.
 * The same object is mutated in place by `refresh()`, allowing callers to hold
 * a stable reference and observe updates after a new repository is cloned or
 * a driver is installed mid-session.
 * @docLink packages/core/concepts#runtime-assets-result
 */
export interface RuntimeAssetsResult {
  catalogEntries: RuntimeCatalogEntry[];
  implicitRefs: RuntimeAssetRef[];
  requiredNpmDeps: RuntimeNpmDeps;
  warnings: RuntimeAssetWarning[];
  /** Re-scan repositories and re-derive implicit refs. Mutates in place. */
  refresh(): Promise<void>;
  /**
   * Catalog lookup. When `kind` is omitted and multiple entries share the
   * `name`, returns `undefined` (caller must disambiguate).
   */
  findEntry(query: { kind?: "connector"; name: string }): RuntimeCatalogEntry | undefined;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve all runtime assets visible to a project by scanning declared repositories.
 *
 * Reads `skaile.yaml`, scans every declared repository (plus the implicit
 * built-in `factory-assets` repo), and derives implicit refs from `mounts:` /
 * `connectors:` declarations. Does not clone or install anything — purely a
 * read-only scan of what is already on disk. URL-only repositories that have
 * not been cloned yet are reported as `repo_unavailable` warnings.
 *
 * @param projectDir - Absolute path to the project workspace root
 * @returns `RuntimeAssetsResult` with catalog entries, implicit refs, npm deps, and warnings
 * @docLink packages/core/concepts#resolve-runtime-assets
 */
export async function resolveRuntimeAssets(projectDir: string): Promise<RuntimeAssetsResult> {
  const result: RuntimeAssetsResult = {
    catalogEntries: [],
    implicitRefs: [],
    requiredNpmDeps: { required: [], optional: [] },
    warnings: [],
    refresh: async () => {
      await populate(projectDir, result);
    },
    findEntry: (query) => findCatalogEntry(result.catalogEntries, query),
  };

  await populate(projectDir, result);
  return result;
}

/**
 * Resolve the ordered list of on-disk roots for the built-in `factory-assets`
 * tree (built-in connectors + first-party skills + bundles). This is the single
 * shared resolver every consumer goes through, so the env override and the
 * bundled baseline are honoured uniformly.
 *
 * Order (env-first = override-wins):
 * 1. `SKAILE_FACTORY_ASSETS_DIR` env dir (legacy: `SKAILE_BASE_ASSETS_DIR`),
 *    when set and existing — an additive override layer.
 * 2. {@link resolvePackagedAssetsDir} — the bundled tree (in-repo source in dev,
 *    `dist/factory-assets/` after install). Always-present baseline.
 *
 * Returns `[]` only when the package cannot be located at all. De-duplicated, so
 * pointing the env var at the bundled dir collapses to one root.
 *
 * @docLink packages/core/concepts#resolve-factory-assets-root
 */
export function factoryAssetRoots(): string[] {
  const roots: string[] = [];
  const envDir = process.env.SKAILE_FACTORY_ASSETS_DIR ?? process.env.SKAILE_BASE_ASSETS_DIR;
  // Deliberately still synchronous after #454: both candidates are
  // container-local (the image's `/app/factory-assets/`, or the installed
  // package tree resolved by `resolvePackagedAssetsDir`), never the FUSE
  // workspace mount. The mount-reachable probes on this module's scan path —
  // the project-relative `sources[].path` candidates in `resolveRepoDir` — are
  // async and deadline-bounded.
  if (envDir && existsSync(envDir)) roots.push(resolvePath(envDir));
  const bundled = resolvePackagedAssetsDir("factory-assets");
  if (bundled) roots.push(bundled);
  return [...new Set(roots)];
}

/**
 * Resolve the primary on-disk root of the `factory-assets` tree — the first of
 * {@link factoryAssetRoots} (the env override when set, else the bundled tree).
 * Prefer {@link factoryAssetRoots} when you need to scan every layer; this
 * single-dir form is for callers that want one canonical path.
 *
 * @returns Absolute path to the highest-precedence `factory-assets` directory
 * @throws When no root can be located
 * @docLink packages/core/concepts#resolve-factory-assets-root
 */
export function resolveFactoryAssetsRoot(): string {
  const hit = resolveFactoryAssetsRootOrNull();
  if (hit) return hit;

  throw new Error(
    `Cannot resolve the factory-assets directory. ` +
      `Set SKAILE_FACTORY_ASSETS_DIR to the on-disk path, ` +
      `ensure @skaile/workspaces is installed, ` +
      `or run from within the @skaile/workspaces package tree.`,
  );
}

/**
 * Non-throwing variant of {@link resolveFactoryAssetsRoot}: the same
 * env-then-packaged resolution tiers, returning `null` instead of throwing when
 * none locate the directory. Callers that treat a missing factory tree as a
 * soft "not present" (e.g. the asset-manager's implicit-source injection) MUST
 * use this so they honour the `SKAILE_FACTORY_ASSETS_DIR` container override
 * identically to the runtime path.
 */
export function resolveFactoryAssetsRootOrNull(): string | null {
  return factoryAssetRoots()[0] ?? null;
}

/**
 * Resolve the `@skaile/workspaces` package root — the directory containing its
 * `package.json`. Tries `require.resolve` first (works when installed as a
 * dependency *and* in-repo via the self-reference), then walks upward from this
 * module looking for a `package.json` whose `name` is `@skaile/workspaces`
 * (bounded to 8 levels). Returns `null` when neither locates it.
 *
 * The single source of truth for finding content shipped inside the package —
 * the unified `factory-assets/` tree (built-in connectors + first-party skills
 * + bundles) resolves through {@link resolvePackagedAssetsDir} on top of this.
 *
 * Memoized: the package location is immutable for the process lifetime, but
 * this sits on the CLI hot path (every `findSkills` / flow / completion call
 * funnels through here), so the `require.resolve` + upward `package.json` walk
 * runs once. `undefined` = not yet computed.
 *
 * @docLink packages/core/concepts#resolve-factory-assets-root
 */
let workspacesPackageRootCache: string | null | undefined;

export function resolveWorkspacesPackageRoot(): string | null {
  if (workspacesPackageRootCache !== undefined) return workspacesPackageRootCache;
  workspacesPackageRootCache = computeWorkspacesPackageRoot();
  return workspacesPackageRootCache;
}

function computeWorkspacesPackageRoot(): string | null {
  try {
    const require = createRequire(import.meta.url);
    return dirname(require.resolve("@skaile/workspaces/package.json"));
  } catch {
    // Fall through to the workspace walk.
  }
  let dir: string;
  try {
    dir = dirname(fileURLToPath(import.meta.url));
  } catch {
    return null;
  }
  for (let i = 0; i < 8; i++) {
    const pkgPath = join(dir, "package.json");
    if (existsSync(pkgPath)) {
      try {
        const pkg = JSON.parse(readFileSync(pkgPath, "utf-8")) as { name?: string };
        if (pkg.name === "@skaile/workspaces") return dir;
      } catch {
        // Ignore malformed package.json and keep walking.
      }
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

/**
 * Locate a content-asset directory inside the package. Prefers the in-repo
 * **source** dir `<pkgRoot>/<subdir>` when present — in a dev checkout it is the
 * full tree (e.g. `factory-assets/` keeps the connector `.ts` entry files that
 * `dist/` does not) — then the published `dist/<subdir>` copy (what
 * `files: ["dist"]` ships, the only one present after `npm i`). Returns `null`
 * when neither exists.
 *
 * `factory-assets/` resolves to the source tree in dev (code + skills + bundles)
 * and to `dist/factory-assets` post-install (compiled connectors + copied
 * manifests/skills/bundles).
 *
 * @param subdir - `"factory-assets"` | …
 * @docLink packages/core/concepts#resolve-factory-assets-root
 */
export function resolvePackagedAssetsDir(subdir: string): string | null {
  const root = resolveWorkspacesPackageRoot();
  if (!root) return null;
  for (const cand of [join(root, subdir), join(root, "dist", subdir)]) {
    if (existsSync(cand)) return cand;
  }
  return null;
}

// ─── Internals ────────────────────────────────────────────────────────────────

async function populate(projectDir: string, target: RuntimeAssetsResult): Promise<void> {
  target.catalogEntries = [];
  target.implicitRefs = [];
  target.requiredNpmDeps = { required: [], optional: [] };
  target.warnings = [];

  const config = resolveSkWorkspaceConfig(projectDir);
  // Connector catalog scan runs over the project's declared sources[] (resolved
  // to their machine-cache clone paths) plus the implicit factory-assets repo.
  const repositories = buildEffectiveRepositories(sourcesToDeclarations(config.sources));
  const links = readLinks(projectDir);

  // Scan each repository for connector entries.
  for (const [name, decl] of Object.entries(repositories)) {
    const repoDir = await resolveRepoDir(name, decl, projectDir, links);
    if (!repoDir) {
      target.warnings.push({
        code: "repo_unavailable",
        name,
        message: repoUnavailableMessage(name, decl),
      });
      continue;
    }
    try {
      // Both factory layers carry the same `factory-assets` provenance — the
      // override repo is an internal scan-order device, not a distinct source.
      const repoName = name === FACTORY_ASSETS_OVERRIDE_REPO_NAME ? FACTORY_ASSETS_REPO_NAME : name;
      const scanned = await scanDirectory(repoDir, repoName);
      const entries = scanned.filter((e) => e.kind === "connector");
      target.catalogEntries.push(...entries);
    } catch (err) {
      target.warnings.push({
        code: "repo_unavailable",
        name,
        message: `Failed to scan repository "${name}" at ${repoDir}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
    }
  }

  // De-duplicate by (kind, name), keeping the first occurrence. Scan order is
  // declared sources → env override → bundled baseline, so an env-supplied
  // connector manifest overrides the bundled one and the bundled one fills the
  // rest (the override layer can refresh built-in manifests in place).
  const seen = new Set<string>();
  target.catalogEntries = target.catalogEntries.filter((e) => {
    const key = `${e.kind}:${e.name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Derive implicit refs from skaile.yaml connectors.
  for (const c of config.connectors ?? []) {
    target.implicitRefs.push({ kind: "connector", name: c.driver, declarationId: c.id });
  }

  // Validate refs and aggregate npm deps.
  const requiredSet = new Set<string>();
  const optionalSet = new Set<string>();
  for (const ref of target.implicitRefs) {
    const entry = findCatalogEntry(target.catalogEntries, ref);
    if (!entry) {
      target.warnings.push({
        code: "missing_driver",
        declarationId: ref.declarationId,
        name: ref.name,
        message:
          `Unknown ${ref.kind} driver "${ref.name}" referenced by ${ref.kind} "${ref.declarationId}". ` +
          `Run \`skaile ${ref.kind} catalog\` to see available drivers, ` +
          `or declare a repository in skaile.yaml that provides this driver.`,
      });
      continue;
    }
    const npm = (entry.metadata?.npm_deps ?? {}) as { required?: string[]; optional?: string[] };
    for (const p of npm.required ?? []) requiredSet.add(p);
    for (const p of npm.optional ?? []) optionalSet.add(p);
  }
  target.requiredNpmDeps.required = [...requiredSet];
  target.requiredNpmDeps.optional = [...optionalSet];
}

/**
 * Build the effective `repositories` map: caller's declarations first, then the
 * implicit built-in `factory-assets` repos. Each {@link factoryAssetRoots} layer
 * becomes its own repo, the env override inserted **before** the bundled
 * baseline so its connector entries win on the first-match dedup downstream.
 * When the package cannot be located at all, a path-less sentinel under
 * {@link FACTORY_ASSETS_REPO_NAME} drives the `repo_unavailable` warning.
 */
function buildEffectiveRepositories(
  declared: Record<string, SourceDeclaration> | undefined,
): Record<string, SourceDeclaration> {
  const result: Record<string, SourceDeclaration> = { ...(declared ?? {}) };
  const roots = factoryAssetRoots();
  if (roots.length === 0) {
    // Surface as a warning during scan rather than throwing here — let the
    // caller decide whether factory-assets is required. resolveRepoDir() /
    // repoUnavailableMessage re-discover the failure and emit it.
    if (!result[FACTORY_ASSETS_REPO_NAME]) result[FACTORY_ASSETS_REPO_NAME] = { path: "" };
    return result;
  }
  // override-first when both layers exist, so the env root's entries are scanned
  // (and matched) ahead of the bundled baseline.
  const keys =
    roots.length > 1
      ? [FACTORY_ASSETS_OVERRIDE_REPO_NAME, FACTORY_ASSETS_REPO_NAME]
      : [FACTORY_ASSETS_REPO_NAME];
  roots.forEach((dir, i) => {
    const key = keys[i] ?? `${FACTORY_ASSETS_REPO_NAME}:${i}`;
    if (!result[key]) result[key] = { path: dir };
  });
  return result;
}

/**
 * Resolve a repository declaration to an on-disk directory. Order:
 *   1. Linked override (`.skaile/links.yaml`).
 *   2. Local `path` — relative to `projectDir`.
 *   3. Cloned URL repo at `.skaile/cache/sources/<name>/` (must already exist).
 *
 * Returns `null` when none of these resolve. Does not clone.
 *
 * **Async and deadline-bounded since #454.** Every candidate here is a
 * project-relative or operator-supplied path, so a `sources[].path` of
 * `workspace/my-assets` resolves onto the rclone FUSE mount — the same tree the
 * scan that follows walks. Probing it with `existsSync`/`lstatSync` would park
 * the `skaile serve` event loop *before* the (now async) scan ever ran, which
 * would leave the H2 fix meaningless on the very declaration that triggers it.
 * A probe that misses its deadline reads as "unavailable", which is what the
 * caller already does with an absent path — it becomes a `repo_unavailable`
 * warning rather than a stall.
 */
async function resolveRepoDir(
  name: string,
  decl: SourceDeclaration,
  projectDir: string,
  links: Record<string, string>,
): Promise<string | null> {
  // 1. Linked override
  const linked = links[name];
  if (linked && (await pathExists(linked))) return linked;

  // 2. Local path
  if (decl.path) {
    const resolved = resolvePath(projectDir, decl.path);
    return (await pathExists(resolved)) ? resolved : null;
  }

  // 3. URL repo cloned to project's .skaile/cache/sources/<name> — the
  //    project-local clone dir (often a symlink into the global cache, which
  //    lives at getGlobalCacheDir()); NOT the global clone root itself.
  if (decl.url) {
    const projectDest = join(projectDir, ".skaile", "cache", "sources", name);
    // One `lstat` answers both questions the pre-#454 `existsSync` + `lstatSync`
    // pair asked, and `lstat` (not `stat`) keeps the "a symlink into the global
    // cache counts" behaviour intact.
    const found = await tryWithFsDeadline(
      `lstat ${projectDest}`,
      async () => {
        await lstat(projectDest);
        return true;
      },
      false,
    );
    return found ? projectDest : null;
  }

  return null;
}

/** Deadline-bounded `existsSync` replacement — a wedged mount reads as absent. */
function pathExists(path: string): Promise<boolean> {
  return tryWithFsDeadline(
    `stat ${path}`,
    async () => {
      await stat(path);
      return true;
    },
    false,
  );
}

function repoUnavailableMessage(name: string, decl: SourceDeclaration): string {
  if (name === FACTORY_ASSETS_REPO_NAME && !decl.path && !decl.url) {
    return (
      `Cannot resolve the built-in factory-assets. ` +
      `Set SKAILE_FACTORY_ASSETS_DIR or ensure @skaile/workspaces is installed.`
    );
  }
  if (decl.url) {
    return `Repository "${name}" (${decl.url}) is not available. Register it as a Library Source via \`skaile source add\`.`;
  }
  if (decl.path) {
    return `Repository "${name}" path not found: ${decl.path}`;
  }
  return `Repository "${name}" has neither url nor path.`;
}

function findCatalogEntry(
  entries: RuntimeCatalogEntry[],
  query: { kind?: "connector"; name: string },
): RuntimeCatalogEntry | undefined {
  if (query.kind) {
    return entries.find((e) => e.kind === query.kind && e.name === query.name);
  }
  const matches = entries.filter((e) => e.name === query.name);
  if (matches.length === 1) return matches[0];
  // 0 or >1 with no kind — caller must disambiguate.
  return undefined;
}
